import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function ThurrockCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Thurrock Pothole Claims | U-Roads RED Rate Nearly Tripled | Fixtyer"
        description="Thurrock Council reports 25% of unclassified roads in RED condition — up from 10% in 2020 — while operating under Section 114 intervention and earning a RED DfT best-practice scorecard. See what the AMBER rating means for your pothole claim."
        canonical="/councils/thurrock"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Thurrock', url: '/councils/thurrock' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Thurrock has a RED best-practice scorecard — can I still claim?',
            answer: 'Yes. The DfT Best Practice rating is RED, and Thurrock\'s overall rating is AMBER — but your claim still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58, not on scorecard colours alone. Thurrock\'s own transparency report shows 25% of U-roads in RED condition in 2024/25, coarse visual inspections on a two-year rotation across 353km of residential roads, and the council operating under Section 114 intervention that restricts highways funding to statutory and safety activities.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 353km — 64% of Thurrock\'s 552km carriageway network. The council surveys unclassified roads using coarse visual inspection, with 50% of the network surveyed each year following changes in April 2023 — meaning a complete U-road survey every two years. At the last five surveys, RED-condition U-roads rose from 10% in 2020/21 to 26% in 2023/24, settling at 25% in 2024/25 — roughly 88km of estate streets, closes and local routes.',
          },
          {
            question: 'Does Thurrock\'s Section 114 financial situation affect my claim?',
            answer: 'It can provide context, not a guarantee. Thurrock\'s own report states the council is operating under intervention measures that directly affect its ability to pump additional funding into the network and restrict spending to statutory, safety and/or spend-to-save activities. That is the council\'s own explanation for holding assets in a steady state rather than improving them — relevant when arguing whether inspection and repair of your specific defect was reasonable.',
          },
          {
            question: 'Thurrock earns GREEN on spend — does that block my claim?',
            answer: 'No. The DfT Spend scorecard is GREEN, largely reflecting increased capital allocation in 2025/26 (£3.819m from DfT plus £5.26m from other sources). Section 58 turns on whether the specific defect was reasonably inspected and repaired, not on aggregate spend. Thurrock\'s own figures show only 6% of projected 2025/26 maintenance spend classified as preventative versus 12% reactive — and unclassified carriageways face a 370-year renewal rate at current resurfacing levels.',
          },
          {
            question: 'Pothole repairs fell to 3,295 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Thurrock still filled 3,295 potholes in 2024/25 with 98.86% repaired within policy timeframe, plus 903 jet-patcher repairs across 45 roads. The council notes 2024/25 was a particularly challenging year due to resource constraints and organisational change. An average of 200 verified potholes are reported per month via inspections and public reports. The five-year total remains 20,749 pothole repairs.',
          },
          {
            question: 'Do I have to report the pothole to Thurrock before claiming?',
            answer: 'Thurrock investigates reported potholes within three working days and prioritises repairs based on defect size, road category and risk. Reporting via the council\'s online highways tool creates a dated record that strengthens your case if the defect was known before your incident or left unrepaired within a reasonable time. Keep your reference number and any confirmation emails.',
          },
          {
            question: 'Why did Thurrock change its survey coverage in 2023?',
            answer: 'Thurrock\'s own report states that up to March 2023, A and B class roads were surveyed 100% in both directions, C class 100% in one direction, and U class 25%. To accommodate negative opinion around the time to collect 100% of the unclassified network, survey lengths were revised from April 2023 to 50% in both directions for A/B/C roads and 50% for U roads — ensuring a complete network survey every two years. The council acknowledges U-road scoring can be subjective and that scores increased after the review.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Thurrock Council"
        councilSlug="thurrock"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/thurrock"
        headline="Thurrock Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Thurrock</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Thurrock: U-Road Failures Have Nearly Tripled Since 2020
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Thurrock Council&apos;s own condition data shows{' '}
            <strong className="text-rose-400">25% of unclassified roads in RED condition</strong> in 2024/25 — up
            from 10% in 2020/21 — across 353km of residential network surveyed on a two-year rotation. The DfT rates
            overall performance <strong className="text-amber-400">AMBER</strong>, spend{' '}
            <strong className="text-emerald-400">GREEN</strong>, but flags{' '}
            <strong className="text-rose-400">RED on best practice</strong> — while the council operates under{' '}
            <strong className="text-rose-400">Section 114 intervention</strong> restricting highways investment.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">26%</div>
            <div className="text-white font-medium mt-2">Peak U-road RED condition (2023/24)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 10% in 2020/21 on 353km of unclassified roads — roughly 92km at peak in RED condition —
              while the council holds a 370-year renewal rate for U-road carriageway resurfacing at current funding levels.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of DfT-approved survey data from Thurrock&apos;s transparency report — A-roads recovering,
            residential roads deteriorating
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (92km — 17% of network): best in five years</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">4%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">down from 8% peak in 2022/23</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">23%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">73%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 68% in 2022/23</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Principal roads intercepted by the M25, A13 and Dartford Crossing receive SCANNER laser surveys and
              close monitoring. The council states A-road condition is the best it has been in five years — but
              A-roads are less than one-fifth of the borough network.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (107km — 19% of network): stable but amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">79%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">19%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">22%</td>
                    <td className="px-4 py-2 text-center font-bold">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED-condition B/C roads remain low at 3%, but amber roads have risen from 18% to 22% since 2021/22 —
              meaning roughly one in four classified local roads may need maintenance soon.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (353km — 64% of network): deteriorating sharply</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">10%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center text-rose-600 font-bold">19%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">26%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition U-roads have risen two-and-a-half times since 2020/21. The council states scoring of the
              unclassified network can be subjective and that scores increased after its 2023 survey review — but
              also notes it has managed to slowly improve the network as the most recent scores indicate.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend, AMBER roads</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.819m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26 (projected)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.26m</div>
                <div className="text-brand-300 text-sm">Capital from other sources 2025/26 (projected)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">6%</div>
                <div className="text-brand-300 text-sm">Preventative maintenance share (2025/26 projected)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Thurrock&apos;s DfT capital allocation nearly tripled for 2025/26 — earning a{' '}
              <span className="text-emerald-400">GREEN spend scorecard</span>. Yet only 6% of projected maintenance
              spend is preventative versus 12% reactive, and U-road carriageways face a{' '}
              <span className="text-rose-400">370-year renewal rate</span> at current resurfacing levels. More money
              does not automatically mean better residential roads.
            </p>
          </div>
        </div>
      </section>

      {/* Survey methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 353km two-year survey cycle
          </h2>
          <p className="text-surface-600 mb-8">
            How Thurrock measures road condition — and what changed in April 2023
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Before April 2023</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A and B class roads — 100% surveyed in both directions</li>
                <li>• C class roads — 100% surveyed in one direction</li>
                <li>• U class roads — 25% of network surveyed</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">From April 2023</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• A/B/C class roads — 50% surveyed in both directions</li>
                <li>• U class roads — 50% of network surveyed</li>
                <li>• Complete network survey every two years</li>
              </ul>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="To accommodate for negative opinion around length of time to collect 100% of the Unclassified network which was creating questions around the validity of the data, the entire survey network for all categories was reviewed, and following changes were made from April 2023 (to fit budget allocation)."
              source="Thurrock Council Local Highways Maintenance Transparency Report (24 June 2025)"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Thurrock&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it in the surveyed or unsurveyed half?</li>
              <li>• If 25% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• U-roads use coarse visual inspection — how does the council track defects between two-year surveys?</li>
              <li>• Did the 2023 methodology change affect comparability of condition records on your route?</li>
            </ul>
            <p className="text-white font-medium">
              A council surveying half its residential network each year cannot claim continuous knowledge of
              condition on every U-road at every point in the cycle.
            </p>
          </div>
        </div>
      </section>

      {/* Section 114 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 114 and the steady-state strategy
          </h2>
          <p className="text-surface-600 mb-8">
            Thurrock&apos;s own explanation for constrained highways investment
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The Council is operating under intervention measure which directly affect our ability to pump additional funding into the Network and restricts to statutory, safety and/or spend to saving activities (Section 114). We have had to therefore rely upon our Asset Data and life cycling planning tools to focus our funding on holding the Highway Assets in a steady state."
              source="Thurrock Council Local Highways Maintenance Transparency Report (24 June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Thurrock formally acknowledges that financial intervention restricts its ability to invest beyond
                holding highway assets in a steady state — not improving them. That is documented context for why
                U-road RED condition rose from 10% to 26% even as A-roads recovered.
              </p>
              <p className="text-surface-600 text-sm">
                The council combines reactive and preventative maintenance using a palette of treatment types,
                but under Section 114 constraints the priority is statutory safety — not network-wide improvement.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2024/25 treatment outputs</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• 7.6km / 54,000m² carriageway resurfaced</li>
                <li>• 4,166m² footway renewed</li>
                <li>• 19,599m² concrete carriageways micro-surfaced</li>
                <li>• 903 jet-patcher repairs over 45 roads</li>
                <li>• 3,295 pothole repairs — 98.86% within KPI timeframe</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            20,749 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a network producing defects faster than preventative works can prevent them
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,364</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,888</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">4,302</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,900</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,295</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20,749</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~200 verified reports per month</h3>
              <p className="text-surface-600 text-sm">
                Thurrock reports an average of 200 verified potholes per month via inspections and public reports
                for 2024/25 (April–August data). That is roughly 2,400 reportable defects annually before council
                safety inspections add more — on a 552km network where defects routinely form between surveys.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2024/25 resource pressures</h3>
              <p className="text-amber-800 text-sm">
                The council notes 2024/25 was a particularly challenging year due to resource constraints and
                change in organisational structure — yet still achieved 98.86% pothole repair KPI adherence.
                Lower repair counts reflect capacity limits, not absence of defects on the network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Best Practice RED */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why best practice is RED
          </h2>
          <p className="text-surface-600 mb-8">
            What Thurrock&apos;s own report reveals about asset management under financial intervention
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">370-year U-road renewal</h3>
              <p className="text-rose-800 text-sm">
                At £858,000 capital for unclassified carriageways — resurfacing approximately 0.21% of the U-road
                network each year — Thurrock&apos;s own fact sheet calculates a 370-year renewal rate. Preventative
                resurfacing at scale on residential roads is structurally impossible at current levels.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">6% preventative spend</h3>
              <p className="text-rose-800 text-sm">
                Projected 2025/26 maintenance is only 6% preventative versus 12% reactive — down from 18%
                preventative in 2024/25. A network holding steady under Section 114 is running reactively, not
                getting ahead of deterioration.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Subjective U-road scoring</h3>
              <p className="text-rose-800 text-sm">
                The council states U-road scoring can be subjective and uses coarse visual inspection rather than
                SCANNER laser technology. Scores rose after the 2023 survey review — the council acknowledges
                questions around data validity prompted the change.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">AMBER condition, GREEN spend, RED best practice</h3>
            <div className="grid sm:grid-cols-4 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">AMBER</div>
                <div className="text-brand-300 text-sm">Overall</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">AMBER</div>
                <div className="text-brand-300 text-sm">Condition</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">GREEN</div>
                <div className="text-brand-300 text-sm">Spend</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">RED</div>
                <div className="text-brand-300 text-sm">Best practice</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Thurrock is not a low-spend authority in 2025/26 — but the DfT&apos;s{' '}
              <span className="text-rose-400">RED best-practice flag</span> sits alongside Section 114 constraints,
              a two-year survey cycle on 64% of the network, and U-road RED rates that have more than doubled.
              That matters when the council argues it could not reasonably have known about your defect.
            </p>
          </div>
        </div>
      </section>

      {/* What this means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against Thurrock&apos;s RED best-practice scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: GREEN spend is real — but residential road condition and process gaps open specific angles
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — DfT capital allocation nearly tripled to £3.819m for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition at its best in five years (4% RED, 73% green)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 98.86% pothole KPI adherence in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Asset Management and Asset Maintenance strategies
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> DfT-approved annual SCANNER surveys on classified roads
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads and major routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — DfT-flagged weaknesses in network management
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 25% of U-roads in RED condition — up from 10% in 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 64% of network on two-year coarse visual survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Section 114 intervention — council admits holding assets in steady state only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 20,749 pothole repairs in five years — defects forming faster than prevention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 370-year U-road renewal rate at current resurfacing levels
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but RED best practice, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council highways tool) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the two-year survey gap and 25% RED rate are structural arguments</li>
              <li>• Whether your route was in the surveyed or unsurveyed half of the network when damage occurred</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Thurrock&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Thurrock first
          </h2>
          <p className="text-surface-600 mb-6">
            Thurrock investigates reported potholes within three working days and prioritises repairs based on defect
            size, road category and public risk. Reporting via the council&apos;s online highways tool creates a dated
            record useful if the defect was known before your incident, or if the council failed to repair it within
            a reasonable time.
          </p>
          <a
            href="https://www.thurrock.gov.uk/pot-holes/report-potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Thurrock Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the interactive map to pinpoint the location, provide photos and contact details. Keep your reference
            number and check back for progress updates.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Thurrock?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED best-practice scorecard demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No Section 114 context cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rate rise documented</li>
                <li>✅ Two-year survey cycle argued</li>
                <li>✅ 20,749 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Thurrock</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=thurrock"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Thurrock claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Thurrock has a RED best-practice scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Best Practice rating is RED, and Thurrock&apos;s overall rating is AMBER — but your claim
                still turns on whether the specific defect that damaged your vehicle was reasonably inspected and
                repaired under Section 58. Thurrock&apos;s own report shows 25% of U-roads in RED condition, coarse
                visual inspections on a two-year rotation, and Section 114 intervention restricting highways funding.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 353km — 64% of Thurrock&apos;s 552km network — surveyed via coarse visual inspection
                with 50% covered each year since April 2023. RED-condition U-roads rose from 10% in 2020/21 to 26% in
                2023/24, settling at 25% in 2024/25 — roughly 88km of estate streets and local routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Thurrock&apos;s Section 114 financial situation affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can provide context. Thurrock states intervention measures directly affect its ability to pump
                additional funding into the network and restrict spending to statutory, safety and spend-to-save
                activities — its own explanation for holding highway assets in a steady state rather than improving them.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Thurrock earns GREEN on spend — does that block my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The Spend scorecard reflects increased 2025/26 capital allocation (£3.819m DfT plus £5.26m other
                sources). Section 58 turns on the specific defect, not aggregate spend. Only 6% of projected 2025/26
                maintenance is preventative, and U-road carriageways face a 370-year renewal rate at current levels.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 3,295 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Thurrock still filled 3,295 potholes with 98.86% KPI adherence, plus 903 jet-patcher repairs. The
                council notes resource constraints in 2024/25. An average of 200 verified potholes are reported per
                month, and the five-year total remains 20,749 repairs.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I have to report the pothole to Thurrock before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Thurrock investigates reports within three working days. Reporting via the online highways tool creates
                a dated record that strengthens your case if the defect was known before your incident or left
                unrepaired within a reasonable time. Keep your reference number and confirmation emails.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did Thurrock change its survey coverage in 2023?
              </h3>
              <p className="text-surface-600 text-sm">
                The council revised survey lengths from April 2023 to fit budget allocation — 50% of A/B/C roads in
                both directions and 50% of U roads — ensuring a complete network survey every two years. The change
                followed negative opinion about the time to collect 100% of the unclassified network and questions
                around data validity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.thurrock.gov.uk/roads-and-pavements-maintenance/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Thurrock Council Local Highways Maintenance Transparency Report (24 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
